<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Scrollbar } from 'swiper/modules'
import type { IProps } from './CardCarousel.types'

import 'swiper/css'
import 'swiper/css/scrollbar'

withDefaults(defineProps<IProps>(), {
  color: 'white',
})

const modules = [Navigation, Scrollbar]
const breakpoints = {
  0: {
    slidesPerView: 1,
    spaceBetween: 14,
  },
  360: {
    slidesPerView: 1.4,
    spaceBetween: 14,
  },
  480: {
    slidesPerView: 1.8,
    spaceBetween: 14,
  },
  577: {
    slidesPerView: 2.2,
    spaceBetween: 14,
  },
  768: {
    slidesPerView: 2.8,
    spaceBetween: 14,
  },
  992: {
    slidesPerView: 3.3,
    spaceBetween: 14,
  },
  1200: {
    slidesPerView: 3.3,
    spaceBetween: 32,
  },
  1600: {
    slidesPerView: 3.1,
    spaceBetween: 32,
  },
}

</script>

<template>
  <div
    :class="[
      'card-carousel',
      `card-carousel--${color}`
    ]"
  >
    <div class="card-carousel__swiper">
      <h4
        class="card-carousel__header"
        v-html="header"
      />

      <div
        v-if="subheader"
        class="card-carousel__subheader"
        v-html="subheader"
      />

      <swiper
        ref="sliderRef"
        :scrollbar="{ draggable: true }"
        :breakpoints="breakpoints"
        :modules="modules"
        navigation
      >
        <swiper-slide
          v-for="(slide, index) in slides"
          :key="index"
        >
          <ArticleCard
            :image="slide.image"
            :title="slide.title"
            :description="slide.description"
            :url="slide.url"
            :date="slide.date"
            :author="slide.author?.name"
          />
        </swiper-slide>
      </swiper>
    </div>
    
    <UiButton
      v-if="button"
      :to="button.to"
    >
      {{ button.text }}
    </UiButton>
  </div>
</template>

<style lang="scss" scoped>
.card-carousel {
  display: flex;
  flex-direction: column;
  padding: 4rem 0 4rem 2.4rem;

  &--gray {
    background-color: $gray-100;
  }

  @include atM {
    padding: 12rem 0 8rem 2.4rem;
  }

  &__header {
    margin-right: 2.4rem;
    font-weight: 500;
    font-size: 2.8rem;
    line-height: 3.6rem;
    text-align: center;

    @include atM {
      font-size: 4.8rem;
      line-height: 5.6rem;
      text-align: left;
    }

    @include atXL {
      font-size: 5.6rem;
      line-height: 6.8rem;
    }
  }

  &__subheader {
    margin-top: 1.6rem;
    margin-right: 2.4rem;
    font-weight: 500;
    font-size: 2rem;
    line-height: 2.8rem;
    text-align: center;

    @include atM {
      margin-top: 3.2rem;
    }

    @include atXL {
      margin-top: 4rem;
      font-size: 2.4rem;
      line-height: 3.2rem;
    }
  }

  &__swiper {
    max-width: 115.2rem;
    width: 100%;
    margin: 0 auto;

    @include atM {
      max-width: 134rem;
    }
  }

  .button {
    align-self: center;
  }

  :deep() {
    .swiper {
      margin-top: 4rem;
      margin-bottom: 4rem;
      overflow: clip;

      svg {
        display: none;
      }

      @include atM {
        margin-top: 6rem;
        margin-bottom: 6rem;
        overflow-x: visible;
      }

      .swiper-button-prev,
      .swiper-button-next {
        display: none;

        @include atS {
          position: absolute;
          top: 30%;
          z-index: 10;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 6rem;
          height: 6rem;
          background-position: center;
          visibility: hidden;
          cursor: pointer;
          opacity: 0;
          transition: all 0.3s ease-in-out;

          &.swiper-button-disabled {
            display: none;
          }
        }
      }

      .swiper-button-prev {
        @include atS {
          left: 0;
          background-image: url('/icons/icon_slider_arrow.svg');
          transform: rotate(180deg);
        }
      }
      .swiper-button-next {
        @include atS {
          right: 0;
          background-image: url('/icons/icon_slider_arrow.svg');
        }
      }

      &:hover {
        .swiper-button-prev,
        .swiper-button-next {
          visibility: visible;
          opacity: 1;
        }
      }
    }

    .swiper-horizontal {
      padding-bottom: 4rem!important;

      @include atM {
        padding-bottom: 6rem!important;
      }
    }
  }
}

</style>
